<script setup lang="ts">
import type { PdpEcommerceBlockSizeSelectorRedesignProps } from './PdpEcommerceBlockSizeSelectorRedesign.props'
import type { extSize } from '../PdpSizeSelector.props'
import { SIDEBAR_IDS } from '@design-system/data/sidebarIds'
import type { FastCheckoutMethod } from '@design-system/composables/useFastCheckoutMethods'

const { ts } = useI18n()

const props = defineProps<PdpEcommerceBlockSizeSelectorRedesignProps>()
const emit = defineEmits<{
  'update:selectedSizeIndex': [selectedSizeIndex: number | null]
  'update:selectedCountry': [selectedCountry: string]
  'update:selectedExtSku': [selectedExtSku: string]
  'similar-products': []
  'sidebar-closed-after-leave': []
  sendAddToCart: []
}>()

const { isLoading, addToCartPayload } = useAddToCart()
const { openDialog, closeDialog } = useDialog()
const { availableMethods } = useFastCheckoutMethods()
const {
  intent,
  isPreparing,
  isWalletReady,
  isPdpFastCheckoutHidden,
  clearIntent,
} = useFastCheckoutIntent()

const currentSize = ref<number | null>(null)
const isNotSelectedSize = ref(false)

const selectedSizeState = useState<extSize>('selectedSizeState')

const isFooterDisabled = computed(
  () =>
    !!props.selectedExtOutOfStock ||
    !!props.selectedDefaultOrOneSizeOutOfStockCl
)

const confirmSelectedSize = () => {
  if (props.isOneSize && (props.extSizes?.length === 0 || !props.extSizes)) {
    currentSize.value = 0
  }
  if (props.extSizes && props.extSizes.length > 0) {
    currentSize.value = selectedSizeState?.value?.value ?? 0
    updateExtSizeUrl()
  }
  if (currentSize.value !== null) {
    isNotSelectedSize.value = false
    emit('update:selectedSizeIndex', currentSize.value)
    return true
  }
  isNotSelectedSize.value = true
  return false
}

const handlePrimaryClick = async () => {
  if (!confirmSelectedSize()) return
  emit('sendAddToCart')
}

const handleChooserConfirm = () => {
  if (!confirmSelectedSize()) return
  closeDialog(SIDEBAR_IDS.pdpEcommerceBlockSizeSelector)
  openDialog(SIDEBAR_IDS.pdpEcommerceBlockFastCheckout)
}

const handleVisualMethodClick = () => {
  confirmSelectedSize()
}

const selectedSize = computed(() => {
  if (props.extSizes && props.extSizes.length > 0) {
    return selectedSizeState?.value?.label ?? ''
  }
  return (
    props.selectedSizeIndex !== null &&
    props.sizes[props.selectedCountry][props.selectedSizeIndex].size
  )
})

const openDialogIfNotDisabled = (id: string) => {
  if (!props.selectedDefaultOrOneSizeOutOfStockCl && !props.isOneSize) {
    openDialog(id)
  }
}

const emitSimilarProducts = (id: string) => {
  closeDialog(id)
  emit('similar-products')
}

const updateExtSizeUrl = () => {
  if (props.selectedExtSku && !!props.extSizes?.length) {
    const selectedExtSkuPath = props.extSizes.find(
      (obj: extSize) => obj.SKU === props.selectedExtSku
    )?.path
    window.history.replaceState(null, '', selectedExtSkuPath)
  }
}

const handleSidebarClosed = () => {
  clearIntent()
  updateExtSizeUrl()
}

const updateSizeFn = async (event: number | null) => {
  currentSize.value = event
  emit('update:selectedSizeIndex', currentSize.value)
}

watch(
  () => props.selectedSizeIndex,
  newVal => {
    currentSize.value = newVal
    if (newVal !== null) isNotSelectedSize.value = false
  }
)

const isSingleOneSize = computed(
  () => !!props.isOneSize && (!props.extSizes || props.extSizes.length === 0)
)

const oneSizeDims = computed(() =>
  isSingleOneSize.value ? selectedSizeState?.value?.Dims || props.dims : ''
)

const sizeLabel = computed(() => {
  let res = ''
  if (selectedSize.value) {
    if (isSingleOneSize.value) {
      res = props.oneSizeLabel ?? ts('pdp.sizeSelector.oneSize')
    } else {
      res = `${selectedSize.value} (${props.selectedCountry})`
    }
  } else {
    res = ts('select')
  }
  return res
})

const methodContainerId = (method: FastCheckoutMethod) =>
  `pdp-size-${method}-container`

const selectedMethod = computed(() =>
  intent.value?.mode === 'method' ? intent.value.method : null
)
/** PayPal stays clickable: its `createOrder` waits for the order being prepared. */
const isMethodWalletBusy = computed(
  () =>
    isWalletReady.value &&
    isPreparing.value &&
    selectedMethod.value !== 'payPal'
)
const isMethodWalletAwaitingSdk = computed(
  () => !!addToCartPayload.value?.sku_code && !isFooterDisabled.value
)
</script>

<template>
  <div class="w-full">
    <AsideButton
      data-testid="size-selector"
      :class="{
        'cursor-not-allowed': selectedDefaultOrOneSizeOutOfStockCl || isOneSize,
      }"
      class="max-h-[52px]"
      :hide-arrow="isOneSize"
      :aria-label="$ts('accessibility.openSizeSelector')"
      @click="
        () => {
          clearIntent()
          openDialogIfNotDisabled(SIDEBAR_IDS.pdpEcommerceBlockSizeSelector)
        }
      "
    >
      <span>
        <span v-if="oneSizeDims" class="text-light-6">
          {{ oneSizeDims }}
        </span>
        <span v-else>
          <span class="text-medium-6">
            {{ sizeLabelOverride ?? $ts('pdp.sizeSelector.size') }}:
          </span>
          <span class="text-light-6">
            {{ sizeLabel }}
          </span>
        </span>
      </span>
    </AsideButton>
    <OrganismsSidebarSlide
      v-if="!hideSidebarComponent"
      :id="SIDEBAR_IDS.pdpEcommerceBlockSizeSelector"
      :header-props="{
        titleText: $ts('pdp.sizeSelector.selectSize'),
      }"
      :footer-props="
        intent
          ? undefined
          : {
              primaryText: $ts('addToBag'),
              isPrimaryLoading: isLoading,
            }
      "
      :selected-ext-out-of-stock="selectedExtOutOfStock"
      :selected-default-or-one-size-out-of-stock-cl="
        selectedDefaultOrOneSizeOutOfStockCl
      "
      @primary-click="handlePrimaryClick"
      @sidebar-closed="handleSidebarClosed"
      @sidebar-closed-after-leave="$emit('sidebar-closed-after-leave')"
    >
      <template #body>
        <div
          v-if="!extSizes || extSizes.length === 0"
          class="text-primitives-grey-200 text-book-6 md:text-book-5 mb-4"
        >
          {{ sideSlideDescription }}
        </div>
        <p
          v-if="isNotSelectedSize"
          role="alert"
          aria-live="assertive"
          class="text-book-6 text-primitives-red"
        >
          {{ $ts('pdp.sizeSelector.selectSizeError') }}
        </p>

        <PdpSizeSelector
          :sizes="sizes"
          :ext-sizes="extSizes"
          :selected-size-index="currentSize"
          :selected-country="selectedCountry"
          :selected-size="selectedSize"
          :is-one-size="isOneSize"
          :sku-sizes="skuSizes"
          @update:selected-size-index="updateSizeFn($event)"
          @update:selected-country="emit('update:selectedCountry', $event)"
          @update:selected-ext-sku="emit('update:selectedExtSku', $event)"
          @similar-products="
            emitSimilarProducts(SIDEBAR_IDS.pdpEcommerceBlockSizeSelector)
          "
        />
      </template>
      <template v-if="intent && !isPdpFastCheckoutHidden" #footer>
        <div
          class="positive-padding py-md bg-primitives-white sticky bottom-0 left-0 right-0 z-50"
        >
          <PdpEcommerceBlockProductFastCheckoutLogosButton
            v-if="intent.mode === 'chooser'"
            :methods="availableMethods ?? []"
            :disabled="isFooterDisabled"
            @click="handleChooserConfirm"
          />
          <div
            v-else-if="selectedMethod"
            class="h-12"
            :aria-busy="isMethodWalletBusy || undefined"
          >
            <PdpEcommerceBlockProductFastCheckoutWallet
              :method="selectedMethod"
              :is-sdk-ready="isWalletReady && !isFooterDisabled"
              :container-id="methodContainerId(selectedMethod)"
              :is-awaiting-sdk="isMethodWalletAwaitingSdk"
              :disabled="isFooterDisabled || isMethodWalletBusy"
              @click="handleVisualMethodClick"
            />
          </div>
        </div>
      </template>
    </OrganismsSidebarSlide>
  </div>
</template>
